import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { CashierReportResponse, CashierData } from './CashierReportModal';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColGroupDef } from 'ag-grid-community';
import { useWindowSize } from '../../../hooks/useWindowSize';
import { Trans, useTranslation } from 'react-i18next';

interface CashierReportTableProps {
    data: CashierReportResponse;
}

export interface CashierReportTableHandle {
    getCurrentTableData: () => CashierData[];
    getAvailableCoins: () => number[];
}
const CashierReportTable = forwardRef<
    CashierReportTableHandle,
    CashierReportTableProps
>(({ data }, ref) => {
    const availableCoins = useMemo<number[]>(() => {
        const coins = new Set<number>();
        data.forEach((row) => {
            const cashierKeys = Object.keys(row.cashier);
            const cashierAdjustedKeys = Object.keys(row.cashierAdjusted);
            (cashierAdjustedKeys.length > 0
                ? cashierAdjustedKeys
                : cashierKeys
            ).forEach((coin) => {
                coins.add(Number(coin));
            });
        });

        return Array.from(coins.values()).sort((a, b) => a - b);
    }, [data]);
    const { innerWidth } = useWindowSize();
    const { t } = useTranslation();
    const isMobile = useMemo(() => innerWidth < 600, []);
    const grid = useRef<AgGridReact<CashierData>>(null);

    const columnDefs: (ColDef<CashierData> | ColGroupDef<CashierData>)[] = [
        { headerName: t('date'), field: 'ts', cellDataType: 'date' },
        {
            headerName: t('machine'),
            field: 'machine.name',
            cellDataType: 'text',
        },
        {
            headerName: t('location'),
            field: 'location.name',
            cellDataType: 'text',
        },
        {
            headerName: t('user'),
            field: 'user.name',
            valueGetter: (params) =>
                params.data?.user.name ?? `#${params.data?.user.id}`,
            cellDataType: 'text',
        },
        {
            headerName: t('cashier'),
            children: availableCoins.map((coin) => ({
                headerName: String(coin),
                cellDataType: 'number',
                cellEditor: 'agNumberCellEditor',
                cellEditorParams: {
                    min: 0,
                    precision: 0,
                },
                editable: (params) => true, //typeof params.data?.cashierAdjusted[coin] == 'number',
                tooltipValueGetter: (row) =>
                    row.data?.cashier[coin] !==
                        row.data?.cashierAdjusted[coin] &&
                    row.data?.cashier[coin] !== undefined &&
                    row.data?.cashierAdjusted[coin] !== undefined
                        ? t('old_value', { value: row.data?.cashier[coin] })
                        : undefined,
                cellClassRules: {
                    'bg-warning': (row) =>
                        row.data?.cashier[coin] !==
                            row.data?.cashierAdjusted[coin] &&
                        row.data?.cashier[coin] !== undefined &&
                        row.data?.cashierAdjusted[coin] !== undefined,
                },
                width: 75,
                valueGetter: (row) => row.data?.cashierAdjusted[coin] ?? null,
                valueSetter: (params) => {
                    params.data.cashierAdjusted[coin] = params.newValue;
                    return true;
                },
                valueFormatter: (params) =>
                    params.value == null ? '-' : params.value,
            })),
        },
        {
            headerName: t('total'),
            width: 100,
            valueGetter: (row) =>
                !row.data
                    ? 0
                    : Object.keys(row.data.cashierAdjusted)
                          .map(
                              (bn) =>
                                  Number(bn) *
                                  (row.data?.cashierAdjusted[bn] ?? 0),
                          )
                          .reduce((prev, crt) => prev + crt, 0),
            cellClassRules: {
                'bg-danger bg-opacity-75': (row) =>
                    row.value !== row.data?.money,
            },
            cellDataType: 'number',
        },
        {
            headerName: t('value'),
            field: 'money',
            cellDataType: 'number',
            width: 100,
        },
    ];

    useImperativeHandle(ref, () => ({
        getCurrentTableData: () => {
            const rowData: CashierData[] = [];
            if (!grid.current) return rowData;
            grid.current.api.forEachNode((node) => {
                if (node.data) rowData.push(node.data);
            });

            return rowData;
        },
        getAvailableCoins: () => availableCoins,
    }));

    return (
        <AgGridReact<CashierData>
            ref={grid}
            columnDefs={columnDefs}
            enableBrowserTooltips
            dataTypeDefinitions={{
                date: {
                    baseDataType: 'date',
                    extendsDataType: 'date',
                    valueFormatter: (params) => {
                        return new Date(params.value ?? '').toLocaleString();
                    },
                },
            }}
            singleClickEdit={isMobile}
            rowData={data}
        />
    );
});

export default CashierReportTable;
